import { useState } from 'react';

// Flowchart Arrow Component
const Arrow = ({ direction = 'down', className = '' }) => {
  const arrows = {
    down: '↓',
    right: '→',
    left: '←',
    up: '↑'
  };
  return <div className={`text-2xl text-gray-400 ${className}`}>{arrows[direction]}</div>;
};

// Flowchart Step Box Component
const StepBox = ({ title, description, color = 'blue', icon, isStart, isEnd }) => {
  const colors = {
    blue: 'bg-blue-100 border-blue-500 text-blue-800',
    green: 'bg-green-100 border-green-500 text-green-800',
    yellow: 'bg-yellow-100 border-yellow-500 text-yellow-800',
    red: 'bg-red-100 border-red-500 text-red-800',
    purple: 'bg-purple-100 border-purple-500 text-purple-800',
    orange: 'bg-orange-100 border-orange-500 text-orange-800',
    gray: 'bg-gray-100 border-gray-500 text-gray-800',
  };

  const shape = isStart || isEnd ? 'rounded-full' : 'rounded-lg';

  return (
    <div className={`p-4 border-2 ${colors[color]} ${shape} text-center min-w-[160px] max-w-[200px]`}>
      {icon && <div className="text-2xl mb-1">{icon}</div>}
      <div className="font-semibold text-sm">{title}</div>
      {description && <div className="text-xs mt-1 opacity-80">{description}</div>}
    </div>
  );
};

// Module content definitions
const moduleContents = {
  // Purchase > Job Work > Purchase Orders
  'purchase-orders': {
    title: 'Purchase Order (PO) Workflow',
    description: 'Complete flow from PO creation to completion',
    icon: '📋',
    content: (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-2">
          <StepBox title="Start" description="New PO needed" color="gray" icon="🚀" isStart />
          <Arrow />
          <StepBox title="Create PO" description="Select vendor, add items, set quantities & rates" color="blue" icon="📝" />
          <Arrow />
          <div className="bg-yellow-100 border-2 border-yellow-500 p-3 rounded-lg">
            <div className="font-semibold text-yellow-800">User Role?</div>
          </div>
          <div className="flex gap-8 items-start">
            <div className="flex flex-col items-center gap-2">
              <div className="text-sm text-gray-500">Admin</div>
              <Arrow />
              <StepBox title="Direct Approval" description="PO approved immediately" color="green" icon="✅" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="text-sm text-gray-500">Operator</div>
              <Arrow />
              <StepBox title="Pending Approval" description="Goes to approval queue" color="orange" icon="⏳" />
              <Arrow />
              <StepBox title="Admin Reviews" description="Approve or Reject" color="purple" icon="👤" />
            </div>
          </div>
          <Arrow />
          <StepBox title="PO Approved" description="Ready to send material" color="green" icon="✅" />
          <Arrow />
          <StepBox title="Send Material" description="Create challan for dispatch" color="blue" icon="📦" />
          <Arrow />
          <StepBox title="Receive Material" description="Record received quantities" color="blue" icon="📥" />
          <Arrow />
          <StepBox title="Quality Check" description="Accept / Reject / Rework" color="purple" icon="🔍" />
          <Arrow />
          <StepBox title="PO Completed" description="All items received & inspected" color="green" icon="🎉" isEnd />
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-bold text-blue-900 mb-2">💡 Key Points:</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• PO numbers are auto-generated (e.g., PO/AX/2025/0001)</li>
            <li>• Non-admin users need approval for all PO actions</li>
            <li>• Price changes are flagged for admin review</li>
            <li>• Overdue POs are highlighted in red</li>
            <li>• Download PO as PDF anytime</li>
          </ul>
        </div>
      </div>
    )
  },

  // Purchase > Job Work > Challans
  'challans': {
    title: 'Challan (Daily Job Work) Workflow',
    description: 'For daily job work without formal PO',
    icon: '📄',
    content: (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-2">
          <StepBox title="Start" description="Daily job work needed" color="gray" icon="🚀" isStart />
          <Arrow />
          <StepBox title="Create Challan" description="Select vendor, add items with job type" color="blue" icon="📝" />
          <Arrow />
          <StepBox title="Challan Sent" description="Material dispatched to vendor" color="orange" icon="🚚" />
          <Arrow />
          <div className="bg-yellow-100 border-2 border-yellow-500 p-3 rounded-lg">
            <div className="font-semibold text-yellow-800">Receipt Type?</div>
          </div>
          <div className="flex gap-8 items-start mt-2">
            <div className="flex flex-col items-center gap-2">
              <div className="text-sm text-gray-500">Partial</div>
              <Arrow />
              <StepBox title="Partial Receipt" description="Some items received" color="yellow" icon="📦" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="text-sm text-gray-500">Full</div>
              <Arrow />
              <StepBox title="Full Receipt" description="All items received" color="green" icon="✅" />
            </div>
          </div>
          <Arrow />
          <StepBox title="Pending QC" description="Awaiting inspection" color="purple" icon="🔍" />
          <Arrow />
          <StepBox title="QC Complete" description="Accept / Reject / Rework" color="blue" icon="📊" />
          <Arrow />
          <StepBox title="Challan Completed" description="All items processed" color="green" icon="🎉" isEnd />
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <h3 className="font-bold text-green-900 mb-2">💡 Key Points:</h3>
          <ul className="text-sm text-green-800 space-y-1">
            <li>• Challans are for daily job work without formal PO</li>
            <li>• Challan numbers can be edited (custom numbering)</li>
            <li>• Supports partial receipts - receive in batches</li>
            <li>• Each item can have Fresh or Rework material type</li>
            <li>• Total value shown on PDF download</li>
          </ul>
        </div>
      </div>
    )
  },

  // Purchase > Job Work > Vendors
  'vendors': {
    title: 'Vendor Management Workflow',
    description: 'Add vendors and manage their item catalog',
    icon: '🏭',
    content: (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-2">
          <StepBox title="Add Vendor" description="Enter name, contact, GST" color="blue" icon="➕" isStart />
          <Arrow />
          <StepBox title="Add Items" description="Parts this vendor supplies" color="purple" icon="📦" />
          <Arrow />
          <div className="flex gap-4">
            <StepBox title="Job Work" description="Processing services" color="orange" icon="🔧" />
            <StepBox title="Direct Supply" description="Parts vendor makes" color="green" icon="�icing" />
          </div>
          <Arrow />
          <StepBox title="Set Rates" description="Price per piece" color="blue" icon="💰" />
          <Arrow />
          <StepBox title="Create POs/Challans" description="Order from vendor" color="purple" icon="📋" />
          <Arrow />
          <StepBox title="Track Performance" description="Quality scores & metrics" color="green" icon="📊" isEnd />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-bold text-blue-900 mb-2">🔧 Job Work Types:</h3>
            <div className="text-xs text-blue-800 grid grid-cols-2 gap-1">
              <span>• Threading</span><span>• Plating</span>
              <span>• Hardening</span><span>• Painting</span>
              <span>• Heat Treatment</span><span>• Welding</span>
              <span>• Powder Coating</span><span>• Stamping</span>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="font-bold text-green-900 mb-2">📊 Performance Metrics:</h3>
            <ul className="text-xs text-green-800 space-y-1">
              <li>• Quality Score: Overall rating</li>
              <li>• Acceptance Rate: % accepted</li>
              <li>• On-Time Rate: % on time</li>
              <li>• Rework Rate: % rework</li>
            </ul>
          </div>
        </div>
      </div>
    )
  },

  // Purchase > Job Work > Quality Control
  'quality-control': {
    title: 'Quality Control (QC) Workflow',
    description: 'Inspect received material and track rework',
    icon: '🔍',
    content: (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-2">
          <StepBox title="Material Received" description="From vendor via challan" color="blue" icon="📥" isStart />
          <Arrow />
          <StepBox title="Pending QC" description="Appears in QC queue" color="orange" icon="⏳" />
          <Arrow />
          <StepBox title="Start Inspection" description="Check quality" color="purple" icon="🔬" />
          <Arrow />
          <div className="bg-yellow-100 border-2 border-yellow-500 p-3 rounded-lg">
            <div className="font-semibold text-yellow-800">Result?</div>
          </div>
          <div className="flex gap-6 items-start mt-2">
            <div className="flex flex-col items-center gap-2">
              <Arrow />
              <StepBox title="Accepted" description="Move to inventory" color="green" icon="✅" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <Arrow />
              <StepBox title="Rejected" description="Return to vendor" color="red" icon="❌" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <Arrow />
              <StepBox title="Rework" description="Send back for repair" color="yellow" icon="🔄" />
            </div>
          </div>
          <Arrow />
          <StepBox title="QC Complete" description="Results recorded" color="green" icon="📊" isEnd />
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <h3 className="font-bold text-orange-900 mb-2">💡 QC Dispositions:</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="bg-green-100 rounded p-2">
              <strong className="text-green-700">✅ Accepted</strong>
              <p className="text-xs">Quality OK, ready for use</p>
            </div>
            <div className="bg-red-100 rounded p-2">
              <strong className="text-red-700">❌ Rejected</strong>
              <p className="text-xs">Return to vendor</p>
            </div>
            <div className="bg-yellow-100 rounded p-2">
              <strong className="text-yellow-700">🔄 Rework</strong>
              <p className="text-xs">Can be fixed by vendor</p>
            </div>
          </div>
        </div>
      </div>
    )
  },

  // Purchase > Job Work > Approval System
  'approval-system': {
    title: 'Approval System Workflow',
    description: 'How approvals work for non-admin users',
    icon: '✅',
    content: (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-2">
          <StepBox title="User Action" description="Create/Edit/Delete PO or Vendor" color="blue" icon="👤" isStart />
          <Arrow />
          <div className="bg-yellow-100 border-2 border-yellow-500 p-3 rounded-lg">
            <div className="font-semibold text-yellow-800">Is Admin?</div>
          </div>
          <div className="flex gap-12 items-start mt-2">
            <div className="flex flex-col items-center gap-2">
              <div className="text-sm text-green-600 font-medium">Yes</div>
              <Arrow />
              <StepBox title="Direct Execute" description="Action happens now" color="green" icon="⚡" />
            </div>
            <div className="flex flex-col items-center gap-2">
              <div className="text-sm text-orange-600 font-medium">No</div>
              <Arrow />
              <StepBox title="Request Created" description="Goes to queue" color="orange" icon="📋" />
              <Arrow />
              <StepBox title="Admin Dashboard" description="Shows pending" color="purple" icon="📊" />
              <Arrow />
              <div className="flex gap-6">
                <StepBox title="Approved" description="Executed" color="green" icon="✅" />
                <StepBox title="Rejected" description="Cancelled" color="red" icon="❌" />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <h3 className="font-bold text-purple-900 mb-2">💡 What Needs Approval:</h3>
          <ul className="text-sm text-purple-800 space-y-1">
            <li>• <strong>PO Create/Update/Delete</strong> - All PO changes</li>
            <li>• <strong>Vendor Create/Update</strong> - Vendor changes</li>
            <li>• <strong>Price Changes</strong> - When PO price differs from master</li>
          </ul>
        </div>
      </div>
    )
  },

  // Purchase > Job Work > Vendor Performance
  'vendor-performance': {
    title: 'Vendor Performance Dashboard',
    description: 'Track and compare vendor quality metrics',
    icon: '📊',
    content: (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-2">
          <StepBox title="Vendor Transactions" description="POs & Challans processed" color="blue" icon="📋" isStart />
          <Arrow />
          <StepBox title="QC Results Recorded" description="Accept/Reject/Rework data" color="purple" icon="🔍" />
          <Arrow />
          <StepBox title="Metrics Calculated" description="Scores computed" color="orange" icon="🧮" />
          <Arrow />
          <div className="flex gap-4">
            <StepBox title="Quality Score" description="Overall rating" color="green" icon="⭐" />
            <StepBox title="Acceptance %" description="Items accepted" color="blue" icon="✅" />
            <StepBox title="On-Time %" description="Delivered on time" color="purple" icon="⏰" />
          </div>
          <Arrow />
          <StepBox title="Rankings" description="Vendors ranked by score" color="green" icon="🏆" isEnd />
        </div>
        <div className="bg-blue-50 rounded-lg p-4">
          <h3 className="font-bold text-blue-900 mb-2">💡 Score Calculation:</h3>
          <div className="text-sm text-blue-800">
            <p><strong>Quality Score = </strong></p>
            <p className="ml-4">60% × Acceptance Rate +</p>
            <p className="ml-4">20% × On-Time Rate +</p>
            <p className="ml-4">20% × (100 - Rework Rate)</p>
          </div>
        </div>
      </div>
    )
  },

  // Masters > Parts Master
  'parts-master': {
    title: 'Parts Master Workflow',
    description: 'Manage parts and assemblies with vendor linking',
    icon: '⚙️',
    content: (
      <div className="space-y-6">
        <div className="flex flex-col items-center gap-2">
          <StepBox title="Add Part" description="Enter item code & part name" color="blue" icon="➕" isStart />
          <Arrow />
          <div className="text-center bg-gray-100 rounded-lg p-3">
            <div className="text-sm font-medium text-gray-600 mb-2">Assembly Grouping</div>
            <div className="text-xs bg-white rounded p-2">
              <div className="font-bold">Item Code: ITEM-001</div>
              <div className="mt-1 space-y-1">
                <div className="bg-blue-50 rounded p-1">Part: Shaft</div>
                <div className="bg-blue-50 rounded p-1">Part: Bearing</div>
                <div className="bg-blue-50 rounded p-1">Part: Housing</div>
              </div>
            </div>
          </div>
          <Arrow />
          <StepBox title="Set Part Type" description="Outsourced / In-house / Both" color="purple" icon="🏷️" />
          <Arrow />
          <StepBox title="Link to Vendors" description="Add to vendor catalogs" color="orange" icon="🔗" />
          <Arrow />
          <StepBox title="Price History" description="Track rate changes" color="green" icon="📈" />
          <Arrow />
          <StepBox title="Use in POs" description="Select when creating PO" color="blue" icon="📋" isEnd />
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-bold text-gray-900 mb-2">💡 Part Types:</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="bg-blue-100 rounded p-2">
              <strong className="text-blue-800">Outsourced</strong>
              <p className="text-xs">Made by vendors</p>
            </div>
            <div className="bg-green-100 rounded p-2">
              <strong className="text-green-800">In-house</strong>
              <p className="text-xs">Made internally</p>
            </div>
            <div className="bg-purple-100 rounded p-2">
              <strong className="text-purple-800">Both</strong>
              <p className="text-xs">Made both ways</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
};

// Navigation structure
const navigationStructure = [
  {
    id: 'purchase',
    name: 'Purchase',
    icon: '🛒',
    description: 'Procurement and vendor management',
    subModules: [
      {
        id: 'job-work',
        name: 'Job Work',
        icon: '🔧',
        description: 'Outsourced manufacturing processes',
        modules: [
          { id: 'purchase-orders', name: 'Purchase Orders', icon: '📋' },
          { id: 'challans', name: 'Challans', icon: '📄' },
          { id: 'vendors', name: 'Vendors', icon: '🏭' },
          { id: 'quality-control', name: 'Quality Control', icon: '🔍' },
          { id: 'approval-system', name: 'Approval System', icon: '✅' },
          { id: 'vendor-performance', name: 'Vendor Performance', icon: '📊' },
        ]
      },
      // Future sub-modules can be added here
      // { id: 'direct-purchase', name: 'Direct Purchase', icon: '📦', modules: [...] }
    ]
  },
  {
    id: 'masters',
    name: 'Masters',
    icon: '📁',
    description: 'Master data management',
    subModules: [
      {
        id: 'master-data',
        name: 'Master Data',
        icon: '📊',
        description: 'Core reference data',
        modules: [
          { id: 'parts-master', name: 'Parts Master', icon: '⚙️' },
          // Future modules: Customers, Warehouses, Machines, etc.
        ]
      }
    ]
  },
  // Future parent modules
  // { id: 'production', name: 'Production', icon: '�icing', subModules: [...] },
  // { id: 'inventory', name: 'Inventory', icon: '📦', subModules: [...] },
];

export default function HelpCenter() {
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedSubModule, setSelectedSubModule] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);

  const handleParentClick = (parent) => {
    setSelectedParent(parent);
    setSelectedSubModule(null);
    setSelectedModule(null);
  };

  const handleSubModuleClick = (subModule) => {
    setSelectedSubModule(subModule);
    setSelectedModule(null);
  };

  const handleModuleClick = (module) => {
    setSelectedModule(module);
  };

  const handleBack = () => {
    if (selectedModule) {
      setSelectedModule(null);
    } else if (selectedSubModule) {
      setSelectedSubModule(null);
    } else if (selectedParent) {
      setSelectedParent(null);
    }
  };

  const renderBreadcrumb = () => {
    const parts = [];
    parts.push(
      <span 
        key="home" 
        className="cursor-pointer hover:text-red-600"
        onClick={() => { setSelectedParent(null); setSelectedSubModule(null); setSelectedModule(null); }}
      >
        🏠 Help Center
      </span>
    );
    
    if (selectedParent) {
      parts.push(<span key="sep1" className="mx-2">/</span>);
      parts.push(
        <span 
          key="parent" 
          className="cursor-pointer hover:text-red-600"
          onClick={() => { setSelectedSubModule(null); setSelectedModule(null); }}
        >
          {selectedParent.icon} {selectedParent.name}
        </span>
      );
    }
    
    if (selectedSubModule) {
      parts.push(<span key="sep2" className="mx-2">/</span>);
      parts.push(
        <span 
          key="sub" 
          className="cursor-pointer hover:text-red-600"
          onClick={() => { setSelectedModule(null); }}
        >
          {selectedSubModule.icon} {selectedSubModule.name}
        </span>
      );
    }
    
    if (selectedModule) {
      parts.push(<span key="sep3" className="mx-2">/</span>);
      parts.push(
        <span key="module" className="text-red-600 font-semibold">
          {selectedModule.icon} {selectedModule.name}
        </span>
      );
    }
    
    return <div className="text-sm text-gray-600 mb-4">{parts}</div>;
  };

  // Render parent modules selection
  const renderParentSelection = () => (
    <div className="space-y-6">
      <div className="text-center py-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">📖 Help Center</h1>
        <p className="text-gray-600">Select a module to learn how it works</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {navigationStructure.map(parent => (
          <div
            key={parent.id}
            onClick={() => handleParentClick(parent)}
            className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition transform hover:-translate-y-1 border-2 border-transparent hover:border-red-500"
          >
            <div className="text-5xl mb-4">{parent.icon}</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{parent.name}</h2>
            <p className="text-gray-600 mb-4">{parent.description}</p>
            <div className="text-sm text-gray-500">
              {parent.subModules.length} sub-module{parent.subModules.length !== 1 ? 's' : ''}
            </div>
            <div className="mt-4 text-red-600 font-medium">
              Click to explore →
            </div>
          </div>
        ))}
        
        {/* Coming Soon placeholders */}
        <div className="bg-gray-100 rounded-xl shadow p-6 opacity-60">
          <div className="text-5xl mb-4">🏭</div>
          <h2 className="text-2xl font-bold text-gray-500 mb-2">Production</h2>
          <p className="text-gray-400 mb-4">Production planning and tracking</p>
          <div className="mt-4 text-gray-400 font-medium">Coming Soon</div>
        </div>
        
        <div className="bg-gray-100 rounded-xl shadow p-6 opacity-60">
          <div className="text-5xl mb-4">📦</div>
          <h2 className="text-2xl font-bold text-gray-500 mb-2">Inventory</h2>
          <p className="text-gray-400 mb-4">Stock and warehouse management</p>
          <div className="mt-4 text-gray-400 font-medium">Coming Soon</div>
        </div>
      </div>
      
      {/* Quick Reference at bottom */}
      <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">📌 Quick Reference</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-red-50 rounded-lg p-4">
            <h3 className="font-bold text-red-900">🔑 User Roles</h3>
            <ul className="text-sm text-red-800 mt-2 space-y-1">
              <li><strong>Admin:</strong> Full access</li>
              <li><strong>Operator:</strong> Needs approval</li>
            </ul>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <h3 className="font-bold text-blue-900">📅 Date Filters</h3>
            <ul className="text-sm text-blue-800 mt-2 space-y-1">
              <li>• Monthly / Quarterly / Yearly</li>
              <li>• Custom date range</li>
            </ul>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <h3 className="font-bold text-green-900">💰 Currency</h3>
            <ul className="text-sm text-green-800 mt-2 space-y-1">
              <li>• Format: Rs.1,00,000.00</li>
              <li>• Indian numeral system</li>
            </ul>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <h3 className="font-bold text-purple-900">🏢 Multi-Company</h3>
            <ul className="text-sm text-purple-800 mt-2 space-y-1">
              <li>• Axis Industries (AX)</li>
              <li>• Acme Industries (AC)</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  // Render sub-modules selection
  const renderSubModuleSelection = () => (
    <div className="space-y-6">
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition"
      >
        ← Back
      </button>
      
      {renderBreadcrumb()}
      
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 text-white">
        <div className="text-4xl mb-2">{selectedParent.icon}</div>
        <h1 className="text-3xl font-bold mb-2">{selectedParent.name}</h1>
        <p className="text-red-100">{selectedParent.description}</p>
      </div>
      
      <h2 className="text-xl font-bold text-gray-900">Select a Sub-Module:</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {selectedParent.subModules.map(subModule => (
          <div
            key={subModule.id}
            onClick={() => handleSubModuleClick(subModule)}
            className="bg-white rounded-xl shadow-lg p-6 cursor-pointer hover:shadow-xl transition transform hover:-translate-y-1 border-2 border-transparent hover:border-red-500"
          >
            <div className="text-4xl mb-3">{subModule.icon}</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{subModule.name}</h3>
            <p className="text-gray-600 mb-3">{subModule.description}</p>
            <div className="text-sm text-gray-500">
              {subModule.modules.length} module{subModule.modules.length !== 1 ? 's' : ''}
            </div>
            <div className="mt-3 text-red-600 font-medium">
              Click to explore →
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render modules selection
  const renderModuleSelection = () => (
    <div className="space-y-6">
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition"
      >
        ← Back
      </button>
      
      {renderBreadcrumb()}
      
      <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl p-6 text-white">
        <div className="text-4xl mb-2">{selectedSubModule.icon}</div>
        <h1 className="text-3xl font-bold mb-2">{selectedSubModule.name}</h1>
        <p className="text-red-100">{selectedSubModule.description}</p>
      </div>
      
      <h2 className="text-xl font-bold text-gray-900">Select a Module to View Workflow:</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {selectedSubModule.modules.map(module => (
          <div
            key={module.id}
            onClick={() => handleModuleClick(module)}
            className="bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-lg transition border-2 border-transparent hover:border-red-500 flex items-center gap-4"
          >
            <div className="text-3xl">{module.icon}</div>
            <div>
              <h3 className="font-bold text-gray-900">{module.name}</h3>
              <p className="text-sm text-red-600">View flowchart →</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Render module content (flowchart)
  const renderModuleContent = () => {
    const content = moduleContents[selectedModule.id];
    
    return (
      <div className="space-y-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition"
        >
          ← Back to modules
        </button>
        
        {renderBreadcrumb()}
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="bg-gradient-to-r from-red-600 to-red-700 p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="text-4xl">{content?.icon || selectedModule.icon}</div>
              <div>
                <h1 className="text-2xl font-bold">{content?.title || selectedModule.name}</h1>
                <p className="text-red-100">{content?.description || ''}</p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            {content?.content || (
              <div className="text-center py-8 text-gray-500">
                <p>Flowchart coming soon...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      {!selectedParent && renderParentSelection()}
      {selectedParent && !selectedSubModule && renderSubModuleSelection()}
      {selectedSubModule && !selectedModule && renderModuleSelection()}
      {selectedModule && renderModuleContent()}
    </div>
  );
}
